import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';

type Props = {
  createdAt: string;
  targetDurationMs: number;
  status: 'ACTIVE' | 'BREACHED' | 'RESOLVED' | 'ACKNOWLEDGED';
  resolvedAt?: string | null;
  businessHoursEnabled?: boolean;
  compact?: boolean;
};

export function formatTargetDuration(
  totalMinutes: number,
  t?: (key: string, defaultValue: string) => unknown
): string {
  const tr = (key: string, fallback: string) => (t ? String(t(key, fallback)) : fallback);
  const d = tr('DURATION_DAYS_SHORT', 'd');
  const h = tr('DURATION_HOURS_SHORT', 'h');
  const m = tr('DURATION_MINUTES_SHORT', 'm');
  const min = tr('MIN', 'min');
  const minsLabel = tr('MINS', 'mins');

  if (!totalMinutes || totalMinutes <= 0) return `0 ${minsLabel}`;
  const days = Math.floor(totalMinutes / 1440);
  const hrs = Math.floor((totalMinutes % 1440) / 60);
  const mins = totalMinutes % 60;

  if (days > 0) {
    return `${days}${d}${hrs > 0 ? ` ${hrs}${h}` : ''}${mins > 0 ? ` ${mins}${m}` : ''}`;
  }
  if (hrs > 0) {
    return `${hrs}${h}${mins > 0 ? ` ${mins}${m}` : ''}`;
  }
  return mins === 1 ? `1 ${min}` : `${mins} ${minsLabel}`;
}

export function formatSlaDuration(ms: number): string {
  const absoluteMs = Math.abs(ms);
  const totalSecs = Math.floor(absoluteMs / 1000);
  const secs = totalSecs % 60;
  const totalMins = Math.floor(totalSecs / 60);
  const mins = totalMins % 60;
  const totalHours = Math.floor(totalMins / 60);
  const hrs = totalHours % 24;
  const days = Math.floor(totalHours / 24);

  const pad = (n: number) => String(n).padStart(2, '0');

  if (days > 0) {
    return `${days}d ${hrs}h ${pad(mins)}m ${pad(secs)}s`;
  }
  if (hrs > 0) {
    return `${hrs}h ${pad(mins)}m ${pad(secs)}s`;
  }
  return `${pad(mins)}m ${pad(secs)}s`;
}

export function SLATimerProgress({
  createdAt,
  targetDurationMs,
  status,
  resolvedAt,
  businessHoursEnabled = false,
  compact = false,
}: Props) {
  const { t } = useTranslate('translation');
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (status !== 'RESOLVED' && status !== 'ACKNOWLEDGED') {
      const interval = setInterval(() => {
        setNow(new Date());
      }, 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [status]);

  const createdTime = new Date(createdAt).getTime();
  const endTime =
    status === 'RESOLVED' && resolvedAt
      ? new Date(resolvedAt).getTime()
      : now.getTime();
  const elapsedMs = endTime - createdTime;

  const remainingMs = targetDurationMs - elapsedMs;
  const isBreached = status === 'BREACHED' || remainingMs <= 0;

  // Progress capped between 0 and 100
  const progressPct = Math.min(Math.max((elapsedMs / targetDurationMs) * 100, 0), 100);

  // Determine colors based on status and progress
  let progressColor: 'success' | 'warning' | 'error' = 'success';
  if (isBreached) {
    progressColor = 'error';
  } else if (progressPct > 75) {
    progressColor = 'warning';
  }

  return (
    <Box sx={{ minWidth: 160 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'semibold',
            color: isBreached ? 'error.main' : progressPct > 75 ? 'warning.main' : 'text.secondary',
            fontSize: compact ? '0.6875rem' : '0.75rem',
          }}
        >
          {isBreached ? t('BREACHED', 'Breached') : t('REMAINING', 'Remaining')}: {formatSlaDuration(remainingMs)}
          {isBreached && remainingMs < 0 && ` (${t('OVERDUE', 'overdue')})`}
        </Typography>
        {businessHoursEnabled && (
          <Tooltip title={t('BUSINESS_HOURS_ACTIVE_TOOLTIP', 'SLA clock only counts hours within business time. The display below uses wall-clock time.')}>
            <Box sx={{ display: 'inline-flex', alignSelf: 'center' }}>
              <Iconify icon="solar:clock-square-bold-duotone" width={compact ? 12 : 14} sx={{ color: 'info.main' }} />
            </Box>
          </Tooltip>
        )}
      </Stack>

      <LinearProgress
        variant="determinate"
        value={progressPct}
        color={progressColor}
        sx={{ height: compact ? 4 : 6, borderRadius: 3 }}
      />
    </Box>
  );
}
